import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Table } from '..';
import { FREIGHT_HEADERS } from '../../constants/table-headers';
import UseGetFreight from '../../hooks/freights/useGetFreight';
import useGetFreightByDelivery from '../../hooks/freights/useGetFreightByDelivery';
import useGetDeliveriesRequests from '../../hooks/delivery-requests';
import ActivityIndicator from '../ActivityIndicator';
import './styles.scss';
import { CartStatus, ReceiptStatus } from '../../types/drawer.types';
import { Cart } from '../../store/ducks/freight/types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import Modals from '../Modal';
import Pagination from '../atoms/pagination';
import { Box, Grid } from '@material-ui/core';
import usePatchReceiptStatus from '../../hooks/receipts/usePatchReceiptStatus';
import usePatchCartStatus from '../../hooks/carts/usePatchCartStatus';
import useGetCarts, { GetCartsReponse } from '../../hooks/carts/useGetCarts';
import BoxWrapper from '../atoms/boxWrapper';
import Typography from '../atoms/typography';
import StatusTag from '../atoms/statusTag';
import Historic from './components/delivery-history';
import FreightHistoric from './components/freight-history';
import { defaultFormatKilometrageNumbers } from '../../utils/formatKilometrageNumber';
import { freightTypeEnum } from '../../hooks/freights/useConfigureFreight';
import { localDateFormat } from '../../utils/localDateFormat';
import { canEditFreight } from '../../utils/freightEditRules';
import ButtonComponent from '../atoms/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import useDeleteFreight from '../../hooks/freights/useDeleteFreight';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

interface FreightProps {
  onPrevStep: () => void;
  freightId?: string;
  deliveryId?: string;
}

const Freight: React.FC<FreightProps> = ({
  onPrevStep,
  freightId: freightIdFromRoute,
  deliveryId: highlightDeliveryId,
}) => {
  const [effectiveFreightId, setEffectiveFreightId] = useState<string | null>(
    freightIdFromRoute || null,
  );
  const id = effectiveFreightId ?? freightIdFromRoute ?? '';
  const [selectedCart, setSelectedCart] = useState<string>('');
  const [callback, setCallback] = useState<{
    message?: string;
    image?: string;
    callback: () => void;
  }>({ message: '', callback: () => null });
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [showFreightHistory, setShowFreightHistory] = useState<boolean>(false);
  const [isPreparingEdit, setIsPreparingEdit] = useState<boolean>(false);
  const [carts, setCarts] = useState<GetCartsReponse>({
    rows: [],
    totalRecords: 0,
    page: 1,
  });
  const { register } = usePatchCartStatus();
  const { register: patchReceipt } = usePatchReceiptStatus();
  const { execute: getFreight, loading: loadingFreight } = UseGetFreight();
  const { execute: getFreightByDelivery, loading: loadingByDelivery } =
    useGetFreightByDelivery();
  const loading = loadingFreight || loadingByDelivery;
  const { execute: getDeliveryRequest } = useGetDeliveriesRequests();
  const { execute: getCarts } = useGetCarts();
  const { freight } = useSelector((state: ApplicationState) => state.freight);
  const { deleteFreight } = useDeleteFreight();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [status, setStatus] = useState('Todos');
 
  const onCartPatched = async (
    cartId: string,
    cartStatus: CartStatus,
    overrideFreightId?: string,
  ) => {
    const cart = await register({
      status: cartStatus,
      cartId: cartId,
      freightId: overrideFreightId ?? id,
    });
    setCarts(prevCarts => {
      const newCarts = prevCarts.rows.map((oldCart: Cart) => {
        if (cart?.cartId === oldCart.cartId) {
          return {
            ...cart,
          };
        }
        return oldCart;
      });
      return {
        ...prevCarts,
        rows: newCarts,
      };
    });
  };

  const handleFiltersCartStatus = async (value?: any) => {
    setCurrentPage(1);
    setStatus(value);
  };

  const onReturnToOriginCart = (cartId: string) => {
    const cart = carts.rows.find(currentCart => currentCart.cartId === cartId);
    const freightIdFromCart =
      cart?.deliveries?.[0]?.freight?.freightId || undefined;

    setCallback({
      callback: () =>
        onCartPatched(
          cartId,
          CartStatus.RETURNING_TO_ORIGIN,
          freightIdFromCart,
        ),
      message: 'Deseja retornar a entrega para a distribuidora?',
    });
    setIsModal(true);
  };

  const onReturnedCart = (cartId: string) => {
    setCallback({
      callback: () => onCartPatched(cartId, CartStatus.RETURNED),
      message: 'Deseja realizar o recebimento da mercadoria na distribuidora?',
    });
    setIsModal(true);
  };

  const onRetryCart = (cartId: string) => {
    setIsModal(true);
    setCallback({
      callback: () => onCartPatched(cartId, CartStatus.ON_DELIVERY),
      message: 'Deseja reenviar a entrega para o PDV?',
    });
  };

  const handlePrev = () => {
    onPrevStep();
  };

  const onRejectReceipt = (cartId: string) => {
    const receipt = carts.rows.find(cart => cart.cartId === cartId)?.receipt;
    const image = receipt?.images.sort(
      (a: any, b: any) => b.createdAt - a.createdAt,
    )[0]?.urlSmall;
    if (!image) {
      console.error('Invalid image URL provided');
      return;
    }
    setIsModal(true);
    setCallback({
      callback: async () => {
        const result = await patchReceipt({
          cartId,
          receiptId: receipt.receiptId,
          status: ReceiptStatus.REJECTED,
          freightId: id,
        });
        if (!result) return;
        setCarts(prevCarts => {
          const newCarts = prevCarts.rows.map((oldCart: Cart) => {
            if (cartId === oldCart.cartId) {
              return {
                ...oldCart,
                receipt: {
                  ...oldCart.receipt,
                  status: ReceiptStatus.REJECTED,
                },
              };
            }
            return oldCart;
          });
          return {
            ...prevCarts,
            rows: newCarts,
          };
        });
      },
      message: 'Deseja reprovar esse canhoto?',
      image,
    });
  };

  // UseCallback
  const onViewHistoric = (cartId: string) => {
    setSelectedCart(cartId);
    setShowHistory(true);
  };

  const downloadImage = async (imageUrl: string) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Canhoto - ${carts?.rows[0]?.deliveries[0]?.corporateName}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const onViewDownloadImage = async (cartId: string) => {
    setSelectedCart(cartId);
    const imageUrl = carts.rows.find(cart => cart.cartId === cartId)?.receipt
      ?.images[0]?.urlMain;
    if (!imageUrl) {
      console.error('Invalid image URL provided');
      return;
    }
    await downloadImage(imageUrl);
  };

  const handleNewFreightClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isPreparingEdit) return;

    setIsPreparingEdit(true);

    const freightRequestId = freight.freightRequest.freightRequestId;

    const initialValues: any = {
      freightRequestId,
      numberOfHelpers: freight.numberOfHelpers,
      pickUpDate: freight.pickUpDate,
      pickUpTime: localDateFormat(freight.pickUpDate, 'HH:mm'),
      deliveryLimitDate: freight.deliveryLimitDate,
      freightType: freight.freightType,
      vehicleTypeId: freight.vehicleType?.vehicleTypeId,
      driverId: freight.driver?.driverId,
      vehicleId: (freight.vehicle as any)?.id,
      totalWeight: freight.totalWeight,
      totalDeliveries: carts?.totalRecords || 0,
      totalKilometers: freight.totalKilometers,
      freightCost: freight.freightCost,
      freightStatus: freight.freightStatus,
      freightId: id,
    };

    const search = freightRequestId
      ? `?freightRequestId=${freightRequestId}`
      : '';
    history.push(`/home/import${search}`, {
      mode: 'edit',
      startStep: 2,
      initialValues,
      disableVehicleType: !!freight.vehicleType,
    });

    setIsPreparingEdit(false);
  };

  const handleCancelFreightClick = () => {
    setCallback({
      callback: async () => {
        try {
          await deleteFreight(id);
          toast.success('Frete excluído com sucesso!');
          onPrevStep();
        } catch (error) {
          toast.error('Erro ao excluir frete!');
        }
      },
      message: 'Deseja excluir esse frete?',
    });
    setIsModal(true);
  };

  const handleOpenFreightHistory = () => setShowFreightHistory(true);

  useEffect(() => {
    const load = async () => {
      if (highlightDeliveryId) {
        const freightData = await getFreightByDelivery(highlightDeliveryId);
        if (freightData?.freightId) {
          setEffectiveFreightId(freightData.freightId);
        }
        return;
      }
      if (freightIdFromRoute) {
        await getFreight(freightIdFromRoute);
        setEffectiveFreightId(freightIdFromRoute);
      }
    };
    load();
  }, [highlightDeliveryId, freightIdFromRoute]);

  useEffect(() => {
    if (!effectiveFreightId) return;
    getCarts({
      page: currentPage,
      rowsPerPage: 20,
      freightId: effectiveFreightId,
    }).then(result => {
      if (result) setCarts(result);
    });
  }, [effectiveFreightId, currentPage, status]);

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <>
      <Modals
        outlined
        onHide={() => {
          setIsModal(false);
        }}
        show={isModal}
        onConfirmClick={callback.callback}
        message={callback.message}
        imgSrc={callback.image}
      />
      <BoxWrapper>
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <ButtonComponent
            theme="primary"
            mb={5}
            onClick={handleNewFreightClick}
            disabled={
              isPreparingEdit ||
              !canEditFreight(freight.freightType, freight.freightStatus)
            }
          >
            <FontAwesomeIcon icon={solid('pencil')} size="sm" color="#fff" />{' '}
            Editar frete
          </ButtonComponent>
          <ButtonComponent
            theme="red"
            mb={5}
            ml={5}
            onClick={handleCancelFreightClick}
          >
            <FontAwesomeIcon
              icon={solid('times-circle')}
              size="sm"
              color="#fff"
            />{' '}
            Cancelar frete
          </ButtonComponent>
          <ButtonComponent
            theme="outline-primary"
            mb={5}
            ml={5}
            onClick={handleOpenFreightHistory}
            aria-label="Histórico do frete"
            title="Histórico do frete"
          >
            <FontAwesomeIcon
              icon={solid('clock-rotate-left')}
              size="sm"
              color="#ff7605"
            />
          </ButtonComponent>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <Typography fontWeight="700">Número da carga</Typography>
            <Typography>
              {carts.rows[0]?.deliveries[0]?.loadNumber || '---'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Criado em</Typography>
            <Typography>
              {moment(freight.createdAt).format('DD/MM/yyyy') ||
                'Criado em indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Veículo</Typography>
            <Typography>{freight.vehicle?.model || '---'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Placa</Typography>
            <Typography>{freight.vehicle?.plate || '---'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Tipo de veículo</Typography>
            <Typography>{freight.vehicleType?.vehicleType || '---'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Motorista</Typography>
            <Typography>
              {freight.driver?.username || 'Nome indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Telefone</Typography>
            <Typography>
              {freight.driver?.phoneNumber || 'Telefone indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">E-mail</Typography>
            <Typography>
              {' '}
              {freight.driver?.email || 'E-mail indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Data de retirada</Typography>
            <Typography>
              {moment(freight.pickUpDate).format('DD/MM/yyyy') ||
                'Data de retirada indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Horário da retirada</Typography>
            <Typography>
              {localDateFormat(freight.pickUpDate, 'HH:mm') ||
                'Horário da retirada indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Data limite de entrega</Typography>
            <Typography>
              {moment(freight.deliveryLimitDate).format('DD/MM/yyyy') ||
                'Data limite indisponível'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Quilometragem estimada</Typography>
            <Typography>
              {defaultFormatKilometrageNumbers(freight.totalKilometers) ||
                'Quilometragem estimada indisponível'}
            </Typography>
          </Grid>

          {freight.freightType !== freightTypeEnum.INTERNAL && (
            <Grid item xs={4}>
              <Typography fontWeight="700">Valor total do frete</Typography>
              <Typography>
                {freight.freightCost.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                }) || '---'}
              </Typography>
            </Grid>
          )}

          <Grid item xs={4}>
            <Typography fontWeight="700">Peso total (Kg)</Typography>
            <Typography>
              {freight.totalWeight.toLocaleString('pt-br', {
                style: 'unit',
                unit: 'kilogram',
                unitDisplay: 'narrow',
              }) || '---'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Total de entregas</Typography>
            <Typography>---</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Ajudantes</Typography>
            <Typography>{freight.numberOfHelpers || '---'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="700">Status</Typography>
            <Typography>
              {freight.freightStatus ? (
                <StatusTag status={freight.freightStatus} />
              ) : (
                '---'
              )}
            </Typography>
          </Grid>
        </Grid>
        <hr />

        <Row>
          <Col className="container-body" lg="12">
            <Table
              columns={FREIGHT_HEADERS}
              data={carts.rows.map(el => ({
                cartId: el.cartId,
                corporateName: el.deliveries[0].corporateName,
                updatedAt: el.deliveries[0].updatedAt,
                nf: el.receipt.nf,
                address: el.deliveries[0].address,
                cep: el.deliveries[0].cep,
                status: el.cartStatus,
                receiptStatus: el.receipt.status,
                deliveryId: el.deliveries[0]?.deliveryId,
              }))}
              highlightDeliveryId={highlightDeliveryId}
              onViewHistoric={onViewHistoric}
              onViewDownloadImage={onViewDownloadImage}
              onCancelDelivery={onReturnToOriginCart}
              onReturnedDelivery={onReturnedCart}
              onRetryDelivery={onRetryCart}
              onCancelReceipt={onRejectReceipt}
            />

            <Pagination
              currentPage={currentPage}
              totalRecords={carts.totalRecords}
              setCurrentPage={setCurrentPage}
              rowsPerPage={20}
            />
          </Col>
        </Row>
      </BoxWrapper>

      <Historic
        setShowHistory={setShowHistory}
        isOpen={showHistory}
        cartId={selectedCart}
      />

      <FreightHistoric
        setShowHistory={setShowFreightHistory}
        isOpen={showFreightHistory}
      />

      <Grid container justifyContent="space-between">
        <Grid item>
          <ButtonComponent
            theme="outline-primary"
            justifyContent="flex-start"
            mt={5}
            onClick={handlePrev}
          >
            Voltar
          </ButtonComponent>
        </Grid>
      </Grid>
    </>
  );
};

export default Freight;
